<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- Mise en ligne et affichage Section -->
            <DashedCard
                :marginTop="0"
                :marginBottom="30"
                :custom-style="{ padding: normalizeSize(24, true), justifyContent: 'center' }"
            >
                <template #title>
                    <span class="poppinsLight ppv2-text-black">{{ $t('package.step6.onlineDisplay.title') }}</span>
                </template>
                <template #content>
                    <div class="ppv2-space-y-4">
                        <!-- Visibility Toggle -->
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                            <BaseSwitch
                                id="visible_online"
                                v-model="formData.visible_online"
                                :label="$t('package.step6.onlineDisplay.visibilityLabel')"
                                :label-left="null"
                            />
                        </div>

                        <!-- Visibility Toggle -->
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                            <BaseSwitch
                                id="visible_online_pos"
                                v-model="formData.visible_online_pos"
                                :label="$t('package.step6.onlineDisplay.visibilityPosLabel')"
                                :label-left="null"
                            />
                        </div>

                        <!-- Questions Merged Toggle -->
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                            <BaseSwitch
                                id="questions_are_merged"
                                v-model="questionsAreMerged"
                                :label="
                                    $t('package.step6.onlineDisplay.mergeQuestionsLabel') || 'Regrouper les questions identiques'
                                "
                                :label-left="null"
                            />
                        </div>
                    </div>
                </template>
            </DashedCard>

            <!-- Categories Section -->
            <DashedCard
                :marginTop="0"
                :marginBottom="30"
                :custom-style="{ padding: normalizeSize(24, true), justifyContent: 'center' }"
            >
                <template #title>
                    <span class="poppinsLight ppv2-text-black">{{ $t('package.step6.categories.title') }}</span>
                </template>
                <template #content>
                    <div class="ppv2-w-full">
                        <BaseSelect
                            :label="$t('package.step6.categories.label')"
                            v-model="selectedTag"
                            :options="filteredAvailableTags"
                            option-label="name"
                            :reduce="(opt) => opt"
                            :multiple="false"
                            :placeholder="$t('package.step6.categories.placeholder')"
                            class="transparent-select ppv2-w-full"
                            class-added="!ppv2-min-h-[40px] !ppv2-rounded-lg "
                            :customSizeLabel="13"
                            @open="activeField = 'categories'"
                            :floatingLabel="true"
                            append-to-body
                            :fixedDropdown="true"
                        />
                    </div>
                </template>
            </DashedCard>

            <!-- Action Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': !isCreationMode }">
                <BackButton v-if="isCreationMode" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="button" variant="primary" @click="saveAndFinish" :loading="loading">
                    {{ isCreationMode ? $t('experience.finish') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>
        <PreviewPackage :active-field="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import PreviewPackage from './PreviewPackage.vue'
import HeaderStep from './HeaderStep.vue'
import normalizeSize from '../../../../utils/normalizeSize'

export default {
    name: 'PackageStep6',
    data() {
        return {
            activeField: null,
            initialDataString: null,
        }
    },
    components: {
        BaseButton,
        BaseSwitch,
        BaseSelect,
        DashedCard,
        BackButton,
        PreviewPackage,
        HeaderStep,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        isCreationMode: { type: Boolean, default: false },
    },
    computed: {
        ...mapGetters('package', [
            'formData',
            'comboItems',
            'pricing',
            'selectedType',
            'availableTags',
            'isEditMode',
            'loading',
            'parameters',
        ]),
        parameters: {
            get() {
                return this.$store.getters['package/parameters']
            },
            set(value) {
                this.$store.dispatch('package/setParameters', value)
            },
        },
        selectedTag: {
            get() {
                return this.$store.getters['package/selectedTag']
            },
            set(value) {
                this.$store.dispatch('package/setSelectedTag', value)
            },
        },
        formData: {
            get() {
                return this.$store.getters['package/formData']
            },
            set(value) {
                this.$store.dispatch('package/setFormData', value)
            },
        },
        questionsAreMerged: {
            get() {
                return this.parameters.questions_are_merged
            },
            set(value) {
                this.$store.dispatch('package/updateParameterWithSideEffects', {
                    key: 'questions_are_merged',
                    value: value,
                })
            },
        },
        typeLabel() {
            const types = {
                specificPrestation: 'Formule avec activités spécifiques',
                family: 'Formule familiale',
                allPrestations: 'Toutes les prestations',
            }
            return types[this.selectedType] || this.selectedType
        },
        filteredAvailableTags() {
            return this.availableTags
        },
    },
    mounted() {
        this.initialDataString = this.getFormDataString()
    },
    methods: {
        ...mapActions('package', ['savePackage', 'resetStore']),
        normalizeSize,

        getFormDataString() {
            return JSON.stringify({
                visible_online: this.formData.visible_online,
                visible_online_pos: this.formData.visible_online_pos,
                questions_are_merged: this.parameters.questions_are_merged,
                selectedTag: this.selectedTag,
            })
        },

        hasChanges() {
            return this.initialDataString !== this.getFormDataString()
        },

        resetInitialState() {
            this.initialDataString = this.getFormDataString()
        },

        goToList() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },

        async saveAndFinish() {
            try {
                await this.savePackage()
                this.resetInitialState()
                if (!this.isCreationMode) {
                    this.$toast.success(this.$t('package.notifications.success_update'))
                }
                if (this.isCreationMode) {
                    this.$emit('next-step')
                    this.goToList()
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}
</style>
